'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';

interface LinkItem {
  id: string;
  title: string;
  url: string;
  icon: string;
  clicks: number;
  active: boolean;
  platform: string;
}

const initialLinks: LinkItem[] = [
  { id: 'link-001', title: 'Latest YouTube Video', url: 'youtube.com/watch?v=xyz123', icon: '▶', clicks: 247, active: true, platform: 'YouTube' },
  { id: 'link-002', title: 'Instagram Profile', url: 'instagram.com/priya.creates', icon: '📸', clicks: 189, active: true, platform: 'Instagram' },
  { id: 'link-003', title: 'Shop My Merch', url: 'priyacreates.store', icon: '🛍️', clicks: 134, active: true, platform: 'Website' },
  { id: 'link-004', title: 'Join My Newsletter', url: 'substack.com/priya', icon: '💌', clicks: 98, active: true, platform: 'Substack' },
  { id: 'link-005', title: 'WhatsApp Community', url: 'wa.me/+919876543210', icon: '💬', clicks: 76, active: false, platform: 'WhatsApp' },
  { id: 'link-006', title: 'My Podcast', url: 'spotify.com/show/priya', icon: '🎙️', clicks: 54, active: true, platform: 'Spotify' },
  { id: 'link-007', title: 'Twitter / X', url: 'x.com/priya_creates', icon: '🐦', clicks: 41, active: false, platform: 'Twitter' },
];

export default function DashboardLinkManager() {
  const [links, setLinks] = useState<LinkItem[]>(initialLinks);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const toggleActive = (id: string) => {
    setLinks((prev) =>
      prev.map((l) => (l.id === id ? { ...l, active: !l.active } : l))
    );
    const link = links.find((l) => l.id === id);
    if (link) {
      toast.success(`"${link.title}" ${link.active ? 'hidden' : 'made visible'} on your page`);
    }
  };

  const deleteLink = async (id: string) => {
    setDeletingId(id);
    // BACKEND INTEGRATION: DELETE /api/links/:id
    await new Promise((r) => setTimeout(r, 300));
    setLinks((prev) => prev.filter((l) => l.id !== id));
    setDeletingId(null);
    toast.success('Link removed from your bio page');
  };

  const addLink = () => {
    if (!newTitle.trim() || !newUrl.trim()) return;
    const newLink: LinkItem = {
      id: `link-${Date.now()}`,
      title: newTitle,
      url: newUrl,
      icon: '🔗',
      clicks: 0,
      active: true,
      platform: 'Website',
    };
    setLinks((prev) => [...prev, newLink]);
    setNewTitle('');
    setNewUrl('');
    setShowAddForm(false);
    toast.success(`"${newTitle}" added to your bio page`);
  };

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h3 className="text-base font-700 text-foreground">Your Links</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{links.filter((l) => l.active).length} active · {links.length} total</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary flexitems-center gap-1.5 text-sm font-600 px-4 py-2 rounded-xl"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Add Link
        </button>
      </div>

      {/* Add link form */}
      {showAddForm && (
        <div className="px-6 py-4 border-b border-border bg-muted/30 animate-fade-in">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Link title (e.g. My YouTube Channel)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <input
              type="text"
              placeholder="URL (e.g. youtube.com/...)"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              onClick={addLink}
              className="btn-primary px-4 py-2.5 rounded-xl text-sm font-600 whitespace-nowrap"
            >
              Add
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Links list */}
      <ul className="divide-y divide-border">
        {links.map((link) => (
          <li
            key={link.id}
            className={`flex items-center gap-4 px-6 py-3.5 hover:bg-muted/40 transition-all duration-200 group ${
              deletingId === link.id ? 'opacity-0 max-h-0 overflow-hidden py-0' : 'opacity-100'
            }`}
          >
            {/* Drag handle */}
            <div className="text-muted-foreground/30 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 6a2 2 0 110-4 2 2 0 010 4zM8 14a2 2 0 110-4 2 2 0 010 4zM8 22a2 2 0 110-4 2 2 0 010 4zM16 6a2 2 0 110-4 2 2 0 010 4zM16 14a2 2 0 110-4 2 2 0 010 4zM16 22a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </div>

            {/* Icon */}
            <span className="text-xl w-8 flex-shrink-0 text-center">{link.icon}</span>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-600 truncate ${link.active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                {link.title}
              </p>
              <p className="text-xs text-muted-foreground truncate">{link.url}</p>
            </div>

            {/* Clicks */}
            <div className="text-right flex-shrink-0 hidden sm:block">
              <p className="text-sm font-700 text-foreground font-tabular">{link.clicks}</p>
              <p className="text-xs text-muted-foreground">clicks</p>
            </div>

            {/* Platform badge */}
            <span className="hidden md:inline-flex text-xs font-500 px-2 py-1 rounded-full bg-secondary text-secondary-foreground flex-shrink-0">
              {link.platform}
            </span>

            {/* Toggle */}
            <button
              onClick={() => toggleActive(link.id)}
              className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                link.active ? 'bg-primary' : 'bg-border'
              }`}
              aria-label={link.active ? 'Hide link' : 'Show link'}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  link.active ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Edit link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => deleteLink(link.id)}
                className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                title="Delete link — this cannot be undone"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Drag to reorder · Toggle to show/hide</p>
        <Link
          href="/create-profile"
          className="text-xs font-600 text-primary hover:underline"
        >
          Edit profile →
        </Link>
      </div>
    </div>
  );
}